import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  DropdownTitle,
  IconButton,
} from '@ads/components-react';
import {
  ChevronRightIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';
import { ERole } from '@/shared/lib/roles';
import { usePermissions } from '@/shared/store/hooks/usePermissions';
import { Skeleton } from '@/shared/ui/atoms';

import type { ParcelOperation, ParcelSummary } from '../../../types/parcels';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';
import type { ParcelViewer } from '../../../utils/parcelOperationAvailability';
import {
  canReintegrateBillet,
  isParcelOperationAvailable,
} from '../../../utils/parcelOperationAvailability';
import { ContractStatusBadge } from '../../atoms/ContractStatusBadge';
import { ParcelSerialsPanel } from '../ParcelSerialsPanel';
import { PARCEL_OPERATION_GROUPS } from './parcelOperationItems';

export interface ParcelsTableProps {
  readonly parcels: readonly IndividualPaymentParcelViewModel[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly summary: ParcelSummary;
  /**
   * `paymentType` do contrato, do cabeçalho.
   *
   * Restringe "Gerar boleto" à forma de pagamento que a tela exibe no topo: sem
   * isso, contrato em cartão com parcela avulsa em boleto oferecia a ação e
   * contrariava o cabeçalho. Omitido, vale só a regra da parcela.
   */
  readonly contractPaymentType?: number | null | undefined;
  readonly onRetry: () => void;
  /**
   * Operação escolhida no menu da parcela ou na linha de um boleto.
   *
   * O `serial` só vem no segundo caso, e identifica o boleto alvo — o menu da
   * parcela age sobre a parcela inteira.
   */
  readonly onOperation: (operation: ParcelOperation, parcelId: number, serial?: number) => void;
}

/**
 * Casca da linha: expansor, campos e menu de ações.
 *
 * **A tabela só existe quando cabe.** A troca é por _container query_, não por
 * breakpoint de viewport, e a diferença é o que resolve o defeito: a largura
 * disponível depende da sidebar (312px expandida), então um `lg:` de viewport
 * acertaria com ela recolhida e erraria com ela aberta — era o caso do print, com
 * a linha cortada no meio de "Pago". Medindo o próprio card, os dois estados
 * ficam corretos sem a tela saber da sidebar.
 *
 * O corte é 59rem: abaixo disso as seis trilhas iguais ficariam estreitas demais
 * para o conteúdo, e os campos viram blocos rotulados em duas colunas — **o scroll
 * horizontal deixa de existir** em vez de ser empurrado para o operador. Com as
 * trilhas iguais o corte deixou de ser a soma de mínimos e passou a ser a largura
 * a partir da qual seis colunas do mesmo tamanho ainda se leem.
 *
 * São seis campos, não os doze da tela legada: os relacionados ficam empilhados
 * no mesmo bloco — primário em cima, secundário embaixo. Eram sete até a coluna do
 * tipo do plano sair: a grid devolve `planTypeText` nulo, então ela exibia "—" em
 * toda linha, e o Status assumiu a posição. Isso derrubou o corte de 63.5rem para
 * 59rem, e com ele o scroll que sobrava a 1366px com a sidebar aberta.
 *
 * **As seis trilhas de dados têm a mesma largura** (`grid-cols-6`, ou seja
 * `minmax(0, 1fr)` em cada). Só o expansor (2rem) e o menu de ações (2.75rem) são
 * fixos e menores — não carregam texto.
 *
 * Trilhas iguais substituíram os mínimos por conteúdo (`minmax(8rem,1.15fr)` e
 * companhia), e a troca tem um custo conhecido: em container estreito, perto do
 * corte, a linha secundária mais longa ("com desc. R$ 354,00") encosta no limite
 * da própria coluna e trunca. É por isso que todas elas têm `truncate` — e a folga
 * volta sozinha conforme o container cresce. Antes cada coluna reservava o que o
 * próprio conteúdo pedia, o que resolvia a truncagem mas deixava a régua visual
 * irregular.
 *
 * Duas tentativas mais antigas elegeram uma única coluna para absorver a folga —
 * primeiro Parcela, depois Nosso nº — e as duas produziram o mesmo defeito em
 * telas largas: a coluna escolhida inflava para o dobro do necessário e abria um
 * vão entre ela e a vizinha, enquanto o resto ficava apertado.
 *
 * O alinhamento depende de cabeçalho e linhas usarem exatamente as mesmas strings
 * de {@link ROW_SHELL} e {@link FIELDS_GRID}, **e** o mesmo `ROW_PADDING_X`.
 */
const ROW_SHELL =
  'grid grid-cols-[2rem_minmax(0,1fr)_2.75rem] items-start gap-x-micro @[59rem]:items-center';

/**
 * Os seis campos de dados.
 *
 * Abaixo de 59rem de **container** são duas colunas de blocos rotulados; a partir
 * dali, as seis trilhas da tabela. É a mesma árvore de DOM nos dois casos — o que
 * muda é a grade e a visibilidade dos rótulos —, então não há dois caminhos de
 * render para manter em sincronia, nem valor duplicado no DOM.
 *
 * O `items-center` do modo tabela é o que alinha a linha: metade das células tem
 * uma linha de texto e metade tem duas (o número da NF sob o Nosso nº, a data de
 * pagamento sob o vencimento), e o `stretch` padrão do grid encostava as curtas no
 * topo da linha. Empilhado ele não entra — ali cada bloco começa no seu rótulo.
 */
const FIELDS_GRID =
  'grid grid-cols-2 gap-x-micro gap-y-xxxs @[59rem]:grid-cols-6 @[59rem]:items-center @[59rem]:gap-y-none';

/** Padding horizontal comum às linhas — sem ele as colunas desalinham. */
const ROW_PADDING_X = 'px-mini';

/**
 * Alinhamento à direita das colunas de dinheiro — **só no modo tabela**.
 *
 * Empilhado, um valor alinhado à direita ficaria longe do próprio rótulo; a
 * coluna à direita só faz sentido quando os dígitos casam na vertical com os das
 * linhas vizinhas.
 *
 * O `pr-xxs` existe porque uma coluna alinhada à direita encosta na vizinha
 * alinhada à esquerda: o valor termina no limite da própria célula e a data começa
 * no limite da seguinte, então o único respiro é gap + padding — a folga `fr` das
 * telas largas não ajuda, porque cresce **dentro** das células. Somado ao
 * `gap-x-micro`, dá 2.25rem nos dois pontos de colisão, contra 0.75rem no resto —
 * o valor fica visivelmente mais perto do Nosso nº que do Vencimento, que é o que
 * a leitura pede: o par valor/identificador se lê junto.
 */
const MONEY_ALIGN = '@[59rem]:items-end @[59rem]:pr-xxs @[59rem]:text-right';

/** Cabeçalho de coluna de dinheiro — mesmo alinhamento e mesmo padding da célula. */
const MONEY_HEADER = 'pr-xxs text-right ts-subtitle-xxs text-ctx-content-title';

/** Casca do card, idêntica à da tabela da listagem. */
const CARD_CLASS =
  'overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs';

/**
 * Um campo da parcela, com o rótulo que só aparece no modo empilhado.
 *
 * O rótulo vive no DOM nos dois modos e é escondido por CSS a partir de 59rem,
 * onde o cabeçalho da tabela já nomeia a coluna. Escondê-lo em vez de renderizar
 * duas árvores mantém um caminho de render só — e evita o valor aparecer duplicado
 * para leitor de tela e para teste.
 */
function ParcelField({
  label,
  align,
  children,
}: {
  readonly label: string;
  readonly align?: 'money' | undefined;
  readonly children: React.ReactNode;
}) {
  return (
    // `items-start` para o conteúdo abraçar a própria largura em vez de esticar
    // na coluna — sem isso a tag de status ocuparia a célula inteira.
    <div
      className={`flex min-w-0 flex-col items-start gap-quark ${
        align === 'money' ? MONEY_ALIGN : ''
      }`.trim()}
    >
      <span className="truncate ts-caption-xxxs text-ctx-content-placeholder @[59rem]:hidden">
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * Cabeçalho da tabela — existe só a partir de 59rem.
 *
 * Abaixo disso não há colunas para nomear: cada campo carrega o próprio rótulo.
 * `hidden` em vez de não renderizar porque o `sticky` depende de o elemento
 * participar do fluxo do container de scroll.
 */
function TableHeader() {
  const { t } = useTranslation('individualPayment');
  return (
    <div
      className={`${ROW_SHELL} ${ROW_PADDING_X} sticky top-none hidden border-b-hairline border-ctx-layout-border bg-ctx-layout-surface py-micro @[59rem]:grid`}
    >
      <div />
      <div className={FIELDS_GRID}>
        <span className="ts-subtitle-xxs text-ctx-content-title">
          {t('parcels.table.columns.type')}
        </span>
        <span className="ts-subtitle-xxs text-ctx-content-title">
          {t('parcels.table.columns.status')}
        </span>
        <span className="ts-subtitle-xxs text-ctx-content-title">
          {t('parcels.table.columns.ourNumber')}
        </span>
        <span className={MONEY_HEADER}>{t('parcels.table.columns.totalValue')}</span>
        <span className="ts-subtitle-xxs text-ctx-content-title">
          {t('parcels.table.columns.expiration')}
        </span>
        <span className={MONEY_HEADER}>{t('parcels.table.columns.totalPaid')}</span>
      </div>
      <span className="sr-only">{t('parcels.table.columns.actions')}</span>
    </div>
  );
}

/**
 * Larguras dos sete campos do skeleton, na ordem das trilhas.
 *
 * Classes literais, não `style` inline: o projeto proíbe estilo inline, e a JIT do
 * Tailwind só gera a arbitrária que aparece escrita no código.
 */
const SKELETON_FIELDS = [
  { key: 'type', width: 'w-[5.5rem]' },
  { key: 'status', width: 'w-[4.5rem]' },
  { key: 'ourNumber', width: 'w-[7.5rem]' },
  { key: 'totalValue', width: 'w-[5.5rem]' },
  { key: 'expiration', width: 'w-[5.5rem]' },
  { key: 'totalPaid', width: 'w-[5.5rem]' },
] as const;

function TableSkeleton() {
  return (
    <div className="flex flex-col divide-y divide-ctx-layout-border">
      {Array.from({ length: 6 }, (_, index) => `parcel-skeleton-${index}`).map(key => (
        <div key={key} className={`${ROW_SHELL} ${ROW_PADDING_X} py-xxs`}>
          <Skeleton className="h-xxs w-xxs rounded-sm" />
          <div className={FIELDS_GRID}>
            {SKELETON_FIELDS.map(field => (
              <Skeleton key={field.key} className={`h-xs rounded-sm ${field.width}`} />
            ))}
          </div>
          <Skeleton className="h-xs w-xs rounded-sm" />
        </div>
      ))}
    </div>
  );
}

/**
 * Menu de operações da parcela, com os itens que esta linha permite.
 *
 * A filtragem vem de {@link isParcelOperationAvailable}, que carrega as condições
 * do grid legado. Grupo que perde todos os itens não é renderizado — um título de
 * seção vazio sugeriria que falta algo carregar.
 */
function ParcelOperationsMenu({
  parcel,
  viewer,
  contractPaymentType,
  onSelect,
}: {
  parcel: IndividualPaymentParcelViewModel;
  viewer: ParcelViewer;
  contractPaymentType?: number | null | undefined;
  onSelect: (operation: ParcelOperation) => void;
}) {
  const { t } = useTranslation('individualPayment');

  const groups = PARCEL_OPERATION_GROUPS.map(group => ({
    titleKey: group.titleKey,
    items: group.items.filter(item =>
      isParcelOperationAvailable(item.operation, parcel, viewer, contractPaymentType),
    ),
  })).filter(group => group.items.length > 0);

  // Sem nenhuma operação disponível o gatilho não é renderizado: um menu que abre
  // vazio é pior que a ausência do botão.
  if (groups.length === 0) return null;

  return (
    // `modal={false}` é obrigatório aqui: quatro itens deste menu abrem o modal
    // de confirmação, e um DropdownMenu modal do Radix aplica
    // `pointer-events: none` no `body` — ao abrir o dialog no mesmo tique, a
    // limpeza dos dois se atropela e a página fica inerte depois de fechar o
    // modal. Sem o modo modal o menu não trava a página e o conflito desaparece;
    // fechar por clique fora e por Escape continua funcionando.
    <Dropdown
      modal={false}
      align="end"
      trigger={
        <IconButton
          ariaLabel={t('parcels.operations.menu')}
          hierarchy="secondary"
          size="sm"
          icon={EllipsisVerticalIcon}
        />
      }
    >
      {groups.flatMap((group, groupIndex) => [
        <DropdownTitle
          key={group.titleKey}
          hasDivider={groupIndex > 0}
          custom={{ content: 'text-nowrap' }}
        >
          {t(group.titleKey)}
        </DropdownTitle>,
        ...group.items.map(item => (
          <DropdownItem
            key={item.operation}
            onSelect={() => onSelect(item.operation)}
            {...('isDestructive' in item ? { status: 'destructive' as const } : {})}
          >
            <DropdownItemContent custom={{ content: 'text-nowrap' }} leadingIcon={item.icon}>
              {t(item.labelKey)}
            </DropdownItemContent>
          </DropdownItem>
        )),
      ])}
    </Dropdown>
  );
}

function ParcelRow({
  parcel,
  isOpen,
  onToggle,
  onOperation,
  viewer,
  contractPaymentType,
}: {
  parcel: IndividualPaymentParcelViewModel;
  isOpen: boolean;
  onToggle: () => void;
  onOperation: (operation: ParcelOperation, serial?: number) => void;
  viewer: ParcelViewer;
  contractPaymentType?: number | null | undefined;
}) {
  const { t } = useTranslation('individualPayment');

  const paidOn = parcel.paymentDate
    ? t('parcels.table.paidOn', { date: formatContractDate(parcel.paymentDate) })
    : null;

  const withDiscount =
    parcel.parcelWithDiscount != null && parcel.parcelWithDiscount !== parcel.total
      ? t('parcels.table.withDiscount', { value: formatCurrency(parcel.parcelWithDiscount) })
      : null;

  // `valueWithFine` só é informativo quando difere do nominal, e aí ele explica
  // por que o valor pago passou do previsto — por isso vive sob "Pago", em vez
  // de ocupar uma coluna própria que ficaria vazia na maioria das linhas.
  const withFine =
    parcel.valueWithFine != null && parcel.valueWithFine !== parcel.total
      ? t('parcels.table.withFine', { value: formatCurrency(parcel.valueWithFine) })
      : null;

  return (
    <div
      className={`${ROW_SHELL} ${ROW_PADDING_X} min-h-[3.5rem] border-b-hairline border-ctx-layout-border py-xxs transition-colors @[59rem]:py-micro ${
        isOpen ? 'bg-ctx-layout-body' : 'bg-ctx-layout-surface hover:bg-ctx-layout-body'
      }`}
    >
      <div className="flex items-center justify-center">
        <IconButton
          icon={ChevronRightIcon}
          hierarchy="tertiary"
          size="sm"
          ariaLabel={isOpen ? t('parcels.table.collapse') : t('parcels.table.expand')}
          aria-expanded={isOpen}
          onClick={onToggle}
          custom={{ icon: isOpen ? 'rotate-90 transition-transform' : 'transition-transform' }}
        />
      </div>

      <div className={FIELDS_GRID}>
        {/* "Tipo" é o `transferType`, que vem pronto como texto na resposta. Ocupa
            o lugar do antigo "Parcela X de Y" — a ordem das linhas já dá o número,
            e o rótulo completo continua no modal de confirmação, onde identifica a
            parcela fora do contexto da lista. */}
        <ParcelField label={t('parcels.table.columns.type')}>
          <span className="truncate ts-paragraph-xxs !font-medium text-ctx-content-title">
            {parcel.transferType ?? EMPTY_VALUE}
          </span>
        </ParcelField>

        {/* Status assumiu esta posição, que era do tipo do plano — a grid devolve
            `planTypeText` nulo, então aquela coluna exibia "—" em toda linha. */}
        <ParcelField label={t('parcels.table.columns.status')}>
          {parcel.statusText ? (
            <ContractStatusBadge status={parcel.statusText} />
          ) : (
            <span className="ts-caption-xxs text-ctx-content-placeholder">{EMPTY_VALUE}</span>
          )}
        </ParcelField>

        {/* Nosso nº: serial em cima, número da NF embaixo. */}
        <ParcelField label={t('parcels.table.columns.ourNumber')}>
          <span className="truncate ts-paragraph-xxs tabular-nums text-ctx-content-title">
            {parcel.serial ?? EMPTY_VALUE}
          </span>
          {parcel.invoiceNumber && (
            <span className="truncate ts-caption-xxs text-ctx-content-placeholder">
              {t('parcels.table.invoice', { number: parcel.invoiceNumber })}
            </span>
          )}
        </ParcelField>

        {/* Valor: nominal em destaque, valor com desconto embaixo. */}
        <ParcelField label={t('parcels.table.columns.totalValue')} align="money">
          <span className="ts-paragraph-xxs !font-semibold tabular-nums text-ctx-content-title">
            {formatCurrency(parcel.total)}
          </span>
          {withDiscount && (
            <span className="truncate ts-caption-xxs text-ctx-content-placeholder">
              {withDiscount}
            </span>
          )}
        </ParcelField>

        {/* Vencimento: data em cima, data de pagamento embaixo. */}
        <ParcelField label={t('parcels.table.columns.expiration')}>
          <span className="ts-paragraph-xxs tabular-nums text-ctx-content-base">
            {formatContractDate(parcel.expirationDate)}
          </span>
          {paidOn && (
            <span className="truncate ts-caption-xxs text-ctx-content-placeholder">{paidOn}</span>
          )}
        </ParcelField>

        {/* Pago: valor pago em destaque, valor com multa embaixo quando houver. */}
        <ParcelField label={t('parcels.table.columns.totalPaid')} align="money">
          <span className="ts-paragraph-xxs !font-semibold tabular-nums text-ctx-content-title">
            {formatCurrency(parcel.totalPaid)}
          </span>
          {withFine && (
            <span className="truncate ts-caption-xxs text-ctx-content-placeholder">{withFine}</span>
          )}
        </ParcelField>
      </div>

      <div className="flex items-center">
        <ParcelOperationsMenu
          parcel={parcel}
          viewer={viewer}
          contractPaymentType={contractPaymentType}
          onSelect={onOperation}
        />
      </div>
    </div>
  );
}

/**
 * Tabela de parcelas de um contrato financeiro.
 *
 * As parcelas vêm de
 * `GET /individualpayment/financial-people/{financialPersonId}/parcels`, que
 * devolve a coleção completa — daí a ausência de paginação. Cada linha expande
 * para o painel de boletos daquela parcela
 * (`GET /individualpayment/parcels/{parcelId}/serials`), carregado sob demanda.
 *
 * Segue o frame `OzPIl` de `design/payment.pen`: nove colunas com células
 * compostas de duas linhas, painel de boletos recuado e rodapé com contagem e
 * totais. A seleção múltipla do design fica para a mudança que implementar as
 * operações em lote — checkboxes sem ação em lote seriam só ruído.
 */
export function ParcelsTable({
  parcels,
  isLoading,
  isError,
  summary,
  contractPaymentType,
  onRetry,
  onOperation,
}: ParcelsTableProps) {
  const { t } = useTranslation('individualPayment');
  const [openIds, setOpenIds] = useState<ReadonlySet<number>>(new Set());

  // Os papéis decidem quais ações a linha oferece — regras herdadas do grid
  // legado. Lidos aqui, uma vez, em vez de por linha.
  const { isMaster, hasRole } = usePermissions();
  const viewer: ParcelViewer = { isMaster, isSecretary: hasRole(ERole.Secretary) };

  const toggle = (parcelId: number) => {
    const next = new Set(openIds);
    if (next.has(parcelId)) next.delete(parcelId);
    else next.add(parcelId);
    setOpenIds(next);
  };

  if (isLoading) {
    return (
      <div className={CARD_CLASS}>
        <div className="@container">
          <TableHeader />
          <TableSkeleton />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={CARD_CLASS}>
        <div className="@container">
          <TableHeader />
        </div>
        <div className="flex flex-col items-center gap-micro p-md">
          <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
            <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
          </span>
          <p className="ts-heading-xs text-ctx-content-title">{t('parcels.table.error')}</p>
          <p className="ts-paragraph-xs text-ctx-content-placeholder">
            {t('parcels.table.errorHint')}
          </p>
          <Button hierarchy="secondary" size="md" onClick={onRetry}>
            {t('parcels.table.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (parcels.length === 0) {
    return (
      <div className={CARD_CLASS}>
        <div className="@container">
          <TableHeader />
        </div>
        <div className="flex flex-col items-center gap-micro p-md">
          <span className="flex items-center justify-center rounded-pill bg-ctx-layout-body p-micro">
            <DocumentTextIcon className="h-xxs w-xxs text-ctx-content-placeholder" />
          </span>
          <p className="ts-heading-xs text-ctx-content-title">{t('parcels.table.empty')}</p>
          <p className="ts-paragraph-xs text-ctx-content-placeholder">
            {t('parcels.table.emptyHint')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={CARD_CLASS}>
      <div className="@container">
        <TableHeader />
        {parcels.map((parcel, index) => {
          const parcelId = parcel.id ?? 0;
          const isOpen = openIds.has(parcelId);
          return (
            <div key={parcel.id ?? `parcel-${index}`}>
              <ParcelRow
                parcel={parcel}
                isOpen={isOpen}
                viewer={viewer}
                contractPaymentType={contractPaymentType}
                onToggle={() => toggle(parcelId)}
                onOperation={operation => onOperation(operation, parcelId)}
              />
              <ParcelSerialsPanel
                parcelId={parcelId}
                isOpen={isOpen}
                canReintegrate={canReintegrateBillet(parcel, viewer)}
                onOperation={(operation, serial) => onOperation(operation, parcelId, serial)}
              />
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-micro border-t-hairline border-ctx-layout-border px-mini py-micro">
        <span className="ts-caption-xxs text-ctx-content-placeholder">
          {t('parcels.table.count', { count: summary.total })}
          {summary.paid + summary.open > 0 &&
            `  ·  ${t('parcels.stats.breakdown', { paid: summary.paid, open: summary.open })}`}
        </span>
        <span className="ts-subtitle-xxs tabular-nums text-ctx-content-base">
          {t('parcels.table.totals', {
            expected: formatCurrency(summary.expectedValue),
            paid: formatCurrency(summary.paidValue),
          })}
        </span>
      </div>
    </div>
  );
}
